import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

/** Purpose: show the selected call's transcript and metadata. Why: agents need a focused view to review an individual voice interaction. */
export default function CallDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedBot, selectedEnv } = useAuth();
  const [call, setCall] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCallDetail();
  }, [id, selectedBot, selectedEnv]);

  /** Purpose: Load fetch call detail for the call detail module. Why: The selected view needs current data to render or continue processing. */
  const fetchCallDetail = async () => {
    try {
      const response = await axios.get(`/api/v1/calls/${id}`, {
        params: {
          botId: selectedBot || '',
          env: selectedEnv || 'dev',
        },
      });
      setCall(response.data.call);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching call detail:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!call) {
    return <Typography>Call not found</Typography>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/calls')}
        sx={{ mb: 2 }}
      >
        Back to Calls
      </Button>
      <Typography variant="h4" gutterBottom>
        Call Details
      </Typography>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Call Information
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography><strong>Call SID:</strong> {call.callSid}</Typography>
          <Typography><strong>Caller:</strong> {call.callerNumber}</Typography>
          {call.botId ? <Typography><strong>Bot:</strong> {call.botId}</Typography> : null}
          {call.envName ? <Typography><strong>Environment:</strong> {call.envName}</Typography> : null}
          <Typography><strong>Direction:</strong> {call.direction}</Typography>
          <Typography><strong>Status:</strong> {call.status}</Typography>
          <Typography><strong>Duration:</strong> {call.duration}s</Typography>
          {Array.isArray(call.flowPath) && call.flowPath.length > 0 ? (
            <Typography><strong>Flow path:</strong> {call.flowPath.join(' -> ')}</Typography>
          ) : null}
          <Typography>
            <strong>Date:</strong> {format(new Date(call.createdAt), 'PPp')}
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Conversation Transcript
        </Typography>
        <Divider sx={{ my: 2 }} />
        {messages.length > 0 ? (
          <Box>
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: msg.speaker === 'bot' ? '#e3f2fd' : '#f5f5f5',
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="caption" color="textSecondary">
                    {msg.speaker === 'bot' ? 'Bot' : 'User'} -{' '}
                    {format(new Date(msg.timestamp), 'PPp')}
                  </Typography>
                  {msg.audioUrl ? (
                    <Chip
                      component="a"
                      clickable
                      icon={<PlayArrowRoundedIcon />}
                      label="Replay"
                      href={msg.audioUrl}
                      target="_blank"
                      rel="noreferrer"
                      size="small"
                      variant="outlined"
                    />
                  ) : null}
                </Stack>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {msg.text}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography>No messages available</Typography>
        )}
      </Paper>
    </Box>
  );
}
